/**
 * Shaping of a closed shift's amounts for the shift report (ShiftsStats).
 *
 * A closeShift's `systemEndAmounts` (built by `countSystemEndAmounts`) holds TWO kinds of key:
 *  - one per payment method id — that method's net sales for the day (`cash`, `kaspi-qr`, …);
 *  - the store's cash WALLET id (`<store>-cash`) — the till the cashier must hold:
 *    opening float + cash sales − returns − supplies − spendings − withdrawals + deposits.
 * `endAmounts` (the counted amounts, CashboxTransactionScreen) writes the counted cash under the
 * wallet id, never under `cash`. So for cash, the till row is the one to reconcile; the `cash`
 * payment-method key is a subtotal already inside it, with no counted counterpart. Rendering it
 * as its own row showed "Наличными" twice — once with the sales subtotal against a fact of 0
 * (ATL-1353).
 *
 * Until ATL-1353 the report's own "close shift" button wrote `endAmounts = systemEndAmounts`, so
 * those closes carry `endAmounts.cash` equal to the subtotal: a copy, not a count, and folded the
 * same way. A `cash` fact that DIFFERS from its expected value is a real count someone entered
 * (older clients wrote one) and keeps its row, so no discrepancy is ever hidden.
 *
 * Closes written before 2025-10-27 stored the till itself under `cash` (and counted cash under
 * `cash` too) with no wallet key; those keep their `cash` row untouched.
 */

/** The store's cash wallet id: where the `cash` payment method deposits for that store. */
export const getCashWallet = (paymentMethods, store) => {
  if (paymentMethods) {
    for (const pm of paymentMethods) {
      if (pm.id === 'cash') {
        return pm.destinationWalletForStore?.[store]?.id
      }
    }
  }
}

/**
 * @param {{ systemEndAmounts?: Record<string, number>, endAmounts?: Record<string, number> | null, store?: string }} shift
 * @param {Array<{ id: string, destinationWalletForStore?: Record<string, { id: string }> }>} paymentMethodsList
 * @param {Record<string, unknown>} paymentMethodsDict payment methods keyed by id
 * @returns {{ systemEndAmounts: Record<string, number>, endAmounts: Record<string, number>, paymentMethodIdMap: Record<string, string> }}
 *   The till first, one row per key, and the payment method each key is labelled with.
 */
export const shapeShiftReportAmounts = (shift, paymentMethodsList, paymentMethodsDict) => {
  const systemEndAmounts = shift.systemEndAmounts || {}
  const endAmounts = shift.endAmounts || {}
  const cashWallet = getCashWallet(paymentMethodsList, shift.store)

  const tillIsKeyed = cashWallet !== undefined && systemEndAmounts[cashWallet] !== undefined
  const cashIsUncounted = endAmounts.cash === undefined || endAmounts.cash === systemEndAmounts.cash
  const keys = Object.keys(systemEndAmounts).filter(key =>
    !(key === 'cash' && tillIsKeyed && cashIsUncounted)
  )
  const sortedKeys = [...keys].sort((a, b) => {
    if (a === cashWallet) return -1
    if (b === cashWallet) return 1
    return 0
  })

  const sortedSystemEndAmounts = {}
  const sortedEndAmounts = {}
  const paymentMethodIdMap = {}
  sortedKeys.forEach(key => {
    // A wallet id is not a payment method; the till is labelled as the cash method.
    paymentMethodIdMap[key] = !paymentMethodsDict?.[key] && key === cashWallet ? 'cash' : key
    sortedSystemEndAmounts[key] = systemEndAmounts[key]
    if (endAmounts[key] !== undefined) {
      sortedEndAmounts[key] = endAmounts[key]
    }
  })

  return {
    systemEndAmounts: sortedSystemEndAmounts,
    endAmounts: sortedEndAmounts,
    paymentMethodIdMap,
  }
}

/**
 * The `endAmounts` of a close nobody counted (the report's "close shift" button): every expected
 * amount taken as fact, keyed the way the counted close (CashboxTransactionScreen) keys them —
 * cash under the till wallet only, never the `cash` sales subtotal as a second cash fact. When the
 * store's till cannot be identified, nothing is dropped.
 *
 * @param {Record<string, number>} systemEndAmounts
 * @param {string | undefined} cashWallet
 * @returns {Record<string, number>}
 */
export const uncountedCloseEndAmounts = (systemEndAmounts, cashWallet) => {
  const endAmounts = { ...systemEndAmounts }
  if (cashWallet !== undefined && systemEndAmounts[cashWallet] !== undefined) {
    delete endAmounts.cash
  }
  return endAmounts
}
